import { isMemoryUIOpen, getLayoutGeometry as getMemoryLayout, getMemoryHoverTipBounds } from "../ui/memoryUI.js";
import { isEscapeUIOpen, ESCAPE_UI_CONFIG } from "../ui/escapeUI.js";
import { isTextboxOpen, getTextboxBounds } from "../ui/textboxUI.js";
import { getGameHudCutouts } from "../ui/gameHud.js";
import { GAME_CONFIG } from "../config/gameConfig.js";
import { getRooms, getRoomAmbientFactor, tileInOtherRoom } from "./rooms.js";
import { isAlarmRoomLocked } from "./alarmLockdown.js";
import { isDebugModeVisible } from "./debugState.js";
import { isLoaded } from "./memory.js";
import { perfBegin, perfEnd, perfFlag } from "./perf.js";

/**
 * 2D Pixel-Art Multiply Environmental Lighting Engine
 *
 * Room fog is binary: only the current Regions mask is ambient-lit.
 * Point lights are screen-blended and clipped to that same mask.
 */

export const LIGHTING_CONFIG = GAME_CONFIG.lighting;

const ROOM_MASK_ALLOWANCE_TILES = 1;
const ROOM_MASK_ALLOWANCE_ALPHA = 0.38;
const LIGHTING_RENDER_INTERVAL = 1 / 30;

let lightsMap = new Map();
let overlayCanvas = null;
let overlayCtx = null;
let animationTime = 0;
let lightingRenderAccumulator = 0;
let cachedGameCanvas = null;
let lastOverlayCss = { top: "", left: "", width: "", height: "" };

function parseHexColor(hex) {
    const h = (hex || "#000000").replace("#", "");
    return {
        r: parseInt(h.slice(0, 2), 16) || 0,
        g: parseInt(h.slice(2, 4), 16) || 0,
        b: parseInt(h.slice(4, 6), 16) || 0,
    };
}

function rgbCss(r, g, b) {
    return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}

function stableNoise(seed) {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
}

function drawScreenRectForWorldRect(worldRect) {
    const tl = toScreen(vec2(worldRect.x, worldRect.y));
    const br = toScreen(vec2(worldRect.x + worldRect.w, worldRect.y + worldRect.h));

    overlayCtx.fillRect(
        Math.floor(Math.min(tl.x, br.x)),
        Math.floor(Math.min(tl.y, br.y)),
        Math.ceil(Math.abs(br.x - tl.x)) + 1,
        Math.ceil(Math.abs(br.y - tl.y)) + 1,
    );
}

function pathScreenRectForWorldRect(worldRect) {
    const tl = toScreen(vec2(worldRect.x, worldRect.y));
    const br = toScreen(vec2(worldRect.x + worldRect.w, worldRect.y + worldRect.h));
    overlayCtx.rect(
        Math.floor(Math.min(tl.x, br.x)) - 1,
        Math.floor(Math.min(tl.y, br.y)) - 1,
        Math.ceil(Math.abs(br.x - tl.x)) + 2,
        Math.ceil(Math.abs(br.y - tl.y)) + 2,
    );
}

function forEachRoomCellRun(room, cells, colOffset, rowOffset, visit) {
    const tw = GAME_CONFIG.level.tileWidth;
    const th = GAME_CONFIG.level.tileHeight;

    for (let row = 0; row < cells.length; row++) {
        let runStart = -1;
        for (let col = 0; col <= cells[row].length; col++) {
            const filled = Boolean(cells[row][col]);
            if (filled && runStart < 0) {
                runStart = col;
            } else if (!filled && runStart >= 0) {
                visit({
                    x: (room.tileRect.x + colOffset + runStart) * tw,
                    y: (room.tileRect.y + rowOffset + row) * th,
                    w: (col - runStart) * tw,
                    h: th,
                });
                runStart = -1;
            }
        }
    }
}

function drawRoomCells(room, cells, colOffset = 0, rowOffset = 0) {
    forEachRoomCellRun(room, cells, colOffset, rowOffset, drawScreenRectForWorldRect);
}

function pathRoomCells(room, cells, colOffset = 0, rowOffset = 0) {
    forEachRoomCellRun(room, cells, colOffset, rowOffset, pathScreenRectForWorldRect);
}

/**
 * Expand region mask for wall-art rim. Never bleed into another room's tiles.
 * Cached on the room object until setRooms() replaces it.
 */
function getExpandedRoomCells(room, allowance) {
    if (!room?.cells) return null;
    if (room.expandedMask && room.expandedMaskAllowance === allowance) {
        return room.expandedMask;
    }
    const cells = room.cells;
    const rows = cells.length;
    const cols = cells.reduce((max, row) => Math.max(max, row.length), 0);
    const expanded = Array.from(
        { length: rows + allowance * 2 },
        () => Array(cols + allowance * 2).fill(false),
    );

    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cells[row].length; col++) {
            if (!cells[row][col]) continue;

            for (let dy = -allowance; dy <= allowance; dy++) {
                for (let dx = -allowance; dx <= allowance; dx++) {
                    const er = row + allowance + dy;
                    const ec = col + allowance + dx;
                    const worldTx = room.tileRect.x - allowance + ec;
                    const worldTy = room.tileRect.y - allowance + er;
                    if (tileInOtherRoom(worldTx, worldTy, room.id)) continue;
                    expanded[er][ec] = true;
                }
            }
        }
    }

    room.expandedMask = expanded;
    room.expandedMaskAllowance = allowance;
    return expanded;
}

function syncOverlayToGameCanvas() {
    if (!overlayCanvas) return;
    if (!cachedGameCanvas || !cachedGameCanvas.isConnected) {
        cachedGameCanvas = document.querySelector("canvas");
    }
    if (!cachedGameCanvas) return;

    const rect = cachedGameCanvas.getBoundingClientRect();
    const top = `${rect.top}px`;
    const left = `${rect.left}px`;
    const widthCss = `${rect.width}px`;
    const heightCss = `${rect.height}px`;
    if (
        top === lastOverlayCss.top
        && left === lastOverlayCss.left
        && widthCss === lastOverlayCss.width
        && heightCss === lastOverlayCss.height
    ) {
        return;
    }

    lastOverlayCss = { top, left, width: widthCss, height: heightCss };
    overlayCanvas.style.top = top;
    overlayCanvas.style.left = left;
    overlayCanvas.style.width = widthCss;
    overlayCanvas.style.height = heightCss;
}

function drawRoomAmbient(room) {
    const tw = GAME_CONFIG.level.tileWidth;
    const th = GAME_CONFIG.level.tileHeight;
    const allowance = ROOM_MASK_ALLOWANCE_TILES;

    overlayCtx.save();
    overlayCtx.globalAlpha = ROOM_MASK_ALLOWANCE_ALPHA;
    if (!room.cells) {
        drawScreenRectForWorldRect({
            x: room.worldRect.x - allowance * tw,
            y: room.worldRect.y - allowance * th,
            w: room.worldRect.w + allowance * tw * 2,
            h: room.worldRect.h + allowance * th * 2,
        });
    } else {
        const expanded = getExpandedRoomCells(room, allowance);
        drawRoomCells(room, expanded, -allowance, -allowance);
    }
    overlayCtx.restore();

    if (!room.cells) {
        drawScreenRectForWorldRect(room.worldRect);
        return;
    }

    drawRoomCells(room, room.cells);
}

/** Clip subsequent draws to the current room mask (+ wall allowance). */
function clipToRoomMask(room) {
    if (!room) return false;

    const allowance = ROOM_MASK_ALLOWANCE_TILES;
    overlayCtx.beginPath();

    if (!room.cells) {
        const tw = GAME_CONFIG.level.tileWidth;
        const th = GAME_CONFIG.level.tileHeight;
        pathScreenRectForWorldRect({
            x: room.worldRect.x - allowance * tw,
            y: room.worldRect.y - allowance * th,
            w: room.worldRect.w + allowance * tw * 2,
            h: room.worldRect.h + allowance * th * 2,
        });
    } else {
        const expanded = getExpandedRoomCells(room, allowance);
        pathRoomCells(room, expanded, -allowance, -allowance);
    }

    overlayCtx.clip();
    return true;
}

function clearUiRect(x, y, w, h, radius = 0) {
    if (!overlayCtx) return;

    overlayCtx.save();
    overlayCtx.globalCompositeOperation = "destination-out";
    overlayCtx.beginPath();

    const r = Math.min(radius, w / 2, h / 2);
    if (r > 0) {
        overlayCtx.moveTo(x + r, y);
        overlayCtx.lineTo(x + w - r, y);
        overlayCtx.quadraticCurveTo(x + w, y, x + w, y + r);
        overlayCtx.lineTo(x + w, y + h - r);
        overlayCtx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        overlayCtx.lineTo(x + r, y + h);
        overlayCtx.quadraticCurveTo(x, y + h, x, y + h - r);
        overlayCtx.lineTo(x, y + r);
        overlayCtx.quadraticCurveTo(x, y, x + r, y);
    } else {
        overlayCtx.rect(x, y, w, h);
    }

    overlayCtx.fill();
    overlayCtx.restore();
}

export function initLighting() {
    overlayCanvas = document.getElementById("lighting-overlay");
    if (!overlayCanvas) {
        overlayCanvas = document.createElement("canvas");
        overlayCanvas.id = "lighting-overlay";
        overlayCanvas.style.position = "fixed";
        overlayCanvas.style.pointerEvents = "none";
        overlayCanvas.style.mixBlendMode = "multiply";
        overlayCanvas.style.zIndex = "8000";
        document.body.appendChild(overlayCanvas);
    }
    overlayCtx = overlayCanvas.getContext("2d");
}

export function hideLightingOverlay() {
    if (!overlayCanvas || !overlayCtx) return;
    overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
    overlayCanvas.style.display = "none";
}

export function showLightingOverlay() {
    if (!overlayCanvas) return;
    overlayCanvas.style.display = "block";
}

export function setLight(id, lightData) {
    lightsMap.set(id, {
        id,
        pos: lightData.pos || { x: 0, y: 0 },
        radius: lightData.radius || 80,
        color: lightData.color || [255, 255, 255],
        intensity: lightData.intensity !== undefined ? lightData.intensity : 1.0,
        pulse: lightData.pulse || false,
        flicker: lightData.flicker || false,
        roomId: lightData.roomId != null ? lightData.roomId : null,
        gid: lightData.gid != null ? lightData.gid : null,
        alarm: Boolean(lightData.alarm),
        clue: Boolean(lightData.clue),
        alarmIntensity: lightData.alarmIntensity ?? 1.0,
        baseIntensity: lightData.baseIntensity !== undefined
            ? lightData.baseIntensity
            : (lightData.intensity !== undefined ? lightData.intensity : 1.0),
    });
}

export function removeLight(id) {
    lightsMap.delete(id);
}

export function clearLights() {
    lightsMap.clear();
}

/** Snapshot of active lights (for debug HUD). */
export function getLightsDebug() {
    return Array.from(lightsMap.values()).map((l) => ({
        id: l.id,
        gid: l.gid,
        roomId: l.roomId,
        pos: { x: l.pos.x, y: l.pos.y },
        color: l.color,
    }));
}

/**
 * Nearest alarm strobe (GID alarm lamps) to fromPos.
 * @param {{ x: number, y: number }} fromPos
 * @param {number|null} [roomId] Prefer lights in this room; falls back to any alarm light.
 */
export function getNearestAlarmLightPos(fromPos, roomId = null) {
    if (!fromPos) return null;

    const pickNearest = (requireRoom) => {
        let best = null;
        let bestDist = Infinity;
        for (const light of lightsMap.values()) {
            if (!light.alarm || !light.pos) continue;
            if (requireRoom && light.roomId !== roomId) continue;
            const dx = light.pos.x - fromPos.x;
            const dy = light.pos.y - fromPos.y;
            const d = dx * dx + dy * dy;
            if (d < bestDist) {
                bestDist = d;
                best = { x: light.pos.x, y: light.pos.y };
            }
        }
        return best;
    };

    if (roomId != null) {
        const inRoom = pickNearest(true);
        if (inRoom) return inRoom;
    }
    return pickNearest(false);
}

/**
 * Resolve Light-layer GID → lamp style from GAME_CONFIG.lighting.lightByGid.
 */
export function getLightStyleForGid(gid) {
    const table = LIGHTING_CONFIG.lightByGid || {};
    const entry = table[gid] || table.default || {};
    return {
        color: entry.color || [255, 224, 150],
        radius: entry.radius ?? LIGHTING_CONFIG.lampRadius ?? 130,
        intensity: entry.intensity ?? 0.9,
        flicker: entry.flicker ?? false,
        pulse: entry.pulse ?? false,
        alarm: Boolean(entry.alarm),
        alarmIntensity: entry.alarmIntensity ?? 1.0,
    };
}

/** True if the Regions room has an active alarm lockdown. */
function isRoomAlerted(roomId) {
    return isAlarmRoomLocked(roomId);
}

/** Alarm lights (e.g. GID 242): off by default, red when the region is alerted. */
function updateAlarmLights() {
    lightsMap.forEach((light) => {
        if (!light.alarm) return;
        const on = isRoomAlerted(light.roomId);
        light.intensity = on ? (light.alarmIntensity ?? 1) : (light.baseIntensity ?? 0);
        light.pulse = on;
        light.flicker = on;
    });
}

/**
 * @param {boolean} visionLoaded
 * @param {{ visibility?: Map<number, number>, currentRoomId?: number|null }} [roomFog]
 * @param {number} [visionFade] - 0..1 cross-fade from black to lit, right after Vision boots.
 */
export function renderLighting(visionLoaded, roomFog = null, visionFade = 1) {
    if (!overlayCanvas || !overlayCtx || overlayCanvas.style.display === "none") return;

    lightingRenderAccumulator += dt();
    if (lightingRenderAccumulator < LIGHTING_RENDER_INTERVAL) return;
    lightingRenderAccumulator = 0;

    // A/B: skip raster work while still ticking the throttle timer above.
    if (!perfFlag("lighting")) return;

    const mark = perfBegin("lighting");

    updateAlarmLights();
    updateClueLights();
    syncOverlayToGameCanvas();

    const w = width();
    const h = height();

    if (overlayCanvas.width !== w || overlayCanvas.height !== h) {
        overlayCanvas.width = w;
        overlayCanvas.height = h;
    }

    animationTime += dt();

    overlayCtx.filter = "none";
    overlayCtx.globalCompositeOperation = "source-over";
    overlayCtx.globalAlpha = 1.0;
    overlayCtx.clearRect(0, 0, w, h);

    const unloaded = parseHexColor(LIGHTING_CONFIG.unloadedColor);
    const roomsList = getRooms();
    const visibility = roomFog?.visibility;
    const useRoomFog = visionLoaded && roomsList.length > 0 && visibility;
    const fade = visionLoaded ? Math.max(0, Math.min(1, visionFade)) : 0;

    // Always paint the dark "unloaded" base first, then cross-fade the lit
    // state in on top — a boot-up fade instead of an instant cut.
    overlayCtx.fillStyle = LIGHTING_CONFIG.unloadedColor;
    overlayCtx.fillRect(0, 0, w, h);

    if (visionLoaded && fade > 0) {
        overlayCtx.save();
        overlayCtx.globalAlpha = fade;

        if (useRoomFog) {
            for (const room of roomsList) {
                const factor = getRoomAmbientFactor(room.id, visibility);
                if (factor <= 0.001) continue;

                const roomAmbientHex = LIGHTING_CONFIG.ambientByRoomId?.[room.id]
                    ?? LIGHTING_CONFIG.ambientColor;
                const roomAmbient = parseHexColor(roomAmbientHex);
                const r = unloaded.r + (roomAmbient.r - unloaded.r) * factor;
                const g = unloaded.g + (roomAmbient.g - unloaded.g) * factor;
                const b = unloaded.b + (roomAmbient.b - unloaded.b) * factor;
                overlayCtx.fillStyle = rgbCss(r, g, b);
                drawRoomAmbient(room);
            }
        } else {
            overlayCtx.fillStyle = LIGHTING_CONFIG.ambientColor;
            overlayCtx.fillRect(0, 0, w, h);
        }

        overlayCtx.restore();

        if (useRoomFog) {
            for (const room of roomsList) {
                const factor = visibility.get(room.id) ?? 0;
                if (factor <= 0.001) continue;

                overlayCtx.save();
                overlayCtx.globalAlpha = fade;
                overlayCtx.globalCompositeOperation = "screen";
                if (clipToRoomMask(room)) {
                    drawActiveLights(room.id, true, factor);
                }
                overlayCtx.restore();
            }
        } else {
            overlayCtx.save();
            overlayCtx.globalAlpha = fade;
            overlayCtx.globalCompositeOperation = "screen";
            drawActiveLights(null, false, 1);
            overlayCtx.restore();
        }

        overlayCtx.globalCompositeOperation = "source-over";
        overlayCtx.globalAlpha = 1.0;
    }

    for (const cut of getGameHudCutouts()) {
        clearUiRect(cut.x, cut.y, cut.w, cut.h, cut.radius ?? 0);
    }

    if (isDebugModeVisible()) {
        clearUiRect(0, h - 120, Math.min(640, w), 120);
    }

    if (isMemoryUIOpen()) {
        const memLayout = getMemoryLayout();
        clearUiRect(memLayout.panelX, memLayout.panelY, memLayout.panelW, memLayout.panelH, 4);
        const tip = getMemoryHoverTipBounds();
        if (tip) clearUiRect(tip.x, tip.y, tip.w, tip.h, tip.radius ?? 3);
    }

    if (isEscapeUIOpen()) {
        const modalW = ESCAPE_UI_CONFIG.modal.width;
        const modalH = ESCAPE_UI_CONFIG.modal.height;
        const modalX = (w - modalW) / 2;
        const modalY = (h - modalH) / 2;
        clearUiRect(modalX, modalY, modalW, modalH, 6);
    }

    if (isTextboxOpen()) {
        const box = getTextboxBounds();
        if (box) clearUiRect(box.x, box.y, box.w, box.h);
    }

    perfEnd(mark);
}

function updateClueLights() {
    const sniffLoaded = isLoaded("action_sniff");
    lightsMap.forEach((light) => {
        if (!light.clue) return;
        light.intensity = sniffLoaded ? (light.baseIntensity ?? 0.52) : 0;
        light.pulse = sniffLoaded;
    });
}

function drawActiveLights(currentRoomId, hardRoomGate, intensityScale = 1) {
    lightsMap.forEach((light) => {
        if (hardRoomGate) {
            if (currentRoomId == null) return;
            if (light.roomId == null || light.roomId !== currentRoomId) return;
        }

        let currentIntensity = light.intensity * intensityScale;
        if (currentIntensity <= 0.001) return;

        const screenPos = toScreen(vec2(light.pos.x, light.pos.y));
        let currentRadius = light.radius;

        if (light.pulse) {
            // Alarm strobes faster / wider than normal lamp pulse
            const speed = light.alarm ? 10.0 : 3.0;
            const amp = light.alarm ? 14 : 6;
            currentRadius += Math.sin(animationTime * speed + light.pos.x) * amp;
            if (light.alarm) {
                const strobe = 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(animationTime * 14));
                currentIntensity *= strobe;
            }
        }

        if (light.flicker) {
            const tick = Math.floor(animationTime * 8);
            const seed = light.pos.x * 12.9898 + light.pos.y * 78.233 + tick * 37.719;
            const flicker = 0.92 + stableNoise(seed) * 0.10;
            currentIntensity *= flicker;
        }

        const maxR = Math.max(1, currentRadius);
        const [lr, lg, lb] = light.color;

        const mix = Math.min(1, currentIntensity);
        const centerR = Math.min(255, Math.round(lr * mix + 255 * (1 - mix * 0.55)));
        const centerG = Math.min(255, Math.round(lg * mix + 255 * (1 - mix * 0.55)));
        const centerB = Math.min(255, Math.round(lb * mix + 255 * (1 - mix * 0.55)));
        const midR = Math.min(255, Math.round(lr * currentIntensity * 0.75));
        const midG = Math.min(255, Math.round(lg * currentIntensity * 0.75));
        const midB = Math.min(255, Math.round(lb * currentIntensity * 0.75));

        const lightGrad = overlayCtx.createRadialGradient(
            screenPos.x, screenPos.y, 0,
            screenPos.x, screenPos.y, maxR,
        );

        lightGrad.addColorStop(0.0, `rgb(${centerR}, ${centerG}, ${centerB})`);
        lightGrad.addColorStop(0.35, `rgb(${midR}, ${midG}, ${midB})`);
        lightGrad.addColorStop(0.7, `rgb(${Math.round(midR * 0.35)}, ${Math.round(midG * 0.35)}, ${Math.round(midB * 0.35)})`);
        lightGrad.addColorStop(1.0, `rgb(0, 0, 0)`);

        overlayCtx.fillStyle = lightGrad;
        overlayCtx.beginPath();
        overlayCtx.arc(screenPos.x, screenPos.y, maxR, 0, Math.PI * 2);
        overlayCtx.fill();
    });
}
